import { getCardinalFromDegrees } from '../map/utils/windUtils';

const COOL_DAY_THRESHOLD_F = 60;

function isoToUnix(iso) {
  if (!iso) return 0;
  try {
    return Math.floor(new Date(iso).getTime() / 1000);
  } catch {
    return 0;
  }
}

function overlapMinutes(aStart, aEnd, bStart, bEnd) {
  const start = Math.max(aStart, bStart);
  const end = Math.min(aEnd, bEnd);
  return Math.max(0, Math.floor((end - start) / 60));
}

/** Python datetime.weekday(): Mon=0 … Sun=6 */
export function pythonWeekdayFromDate(dateIso) {
  if (!dateIso || typeof dateIso !== 'string') return null;
  const [y, m, d] = dateIso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const jsDay = new Date(y, m - 1, d).getDay();
  return (jsDay + 6) % 7;
}

function overlapSolunarDaylight(blocks, sunrise, sunset) {
  if (!sunrise || !sunset) return 0;
  return (blocks || []).reduce(
    (acc, block) => acc + overlapMinutes(
      isoToUnix(block?.start),
      isoToUnix(block?.end),
      sunrise,
      sunset,
    ),
    0,
  );
}

function overlapSolunarWindow(blocks, winStart, winEnd) {
  return (blocks || []).reduce(
    (acc, block) => acc + overlapMinutes(
      isoToUnix(block?.start),
      isoToUnix(block?.end),
      winStart,
      winEnd,
    ),
    0,
  );
}

function isRainyDay(weather) {
  if (!weather) return false;
  if (weather.weather_type === 'rainy') return true;
  const pop = Number(weather.pop);
  return Number.isFinite(pop) && pop >= 0.5;
}

/**
 * Human-readable score contributors aligned with /admin/forecast-settings logic.
 * @returns {{ positive: boolean, label: string }[]}
 */
export function buildDetailScoreFactors(day, { prevDay = null, coolDayThresholdF = COOL_DAY_THRESHOLD_F } = {}) {
  if (!day) return [];

  const bullets = [];
  const w = day.weather || {};
  const factors = day.factors || {};
  const solunar = day.solunar || {};
  const majors = solunar.majors || [];
  const minors = solunar.minors || [];
  const sunrise = Number(w.sunrise) || 0;
  const sunset = Number(w.sunset) || 0;

  if (day.lunar_phase) {
    const lunarPts = Number(factors.lunar);
    bullets.push({
      positive: !Number.isFinite(lunarPts) || lunarPts >= 40,
      label: day.lunar_phase,
    });
  }

  const prevTemp = prevDay?.weather?.temp_day ?? prevDay?.weather?.temp_max;
  const currTemp = w.temp_day ?? w.temp_max;
  if (prevTemp != null && currTemp != null) {
    const delta = currTemp - prevTemp;
    if (delta <= -3) {
      bullets.push({ positive: true, label: `Temps falling ${Math.abs(Math.round(delta))}°` });
    } else if (delta >= 3) {
      bullets.push({ positive: false, label: `Temps rising ${Math.round(delta)}°` });
    } else {
      const tempPts = Number(factors.temp);
      bullets.push({
        positive: Number.isFinite(tempPts) && tempPts >= 50,
        label: 'Stable temps',
      });
    }
  } else if (typeof w.temp_max === 'number') {
    bullets.push({
      positive: w.temp_max <= coolDayThresholdF,
      label: `High ${Math.round(w.temp_max)}°F`,
    });
  }

  const prevPressure = prevDay?.weather?.pressure;
  const pressure = w.pressure;
  if (prevPressure != null && pressure != null) {
    const delta = pressure - prevPressure;
    if (Math.abs(delta) >= 1) {
      bullets.push({
        positive: Math.abs(delta) >= 2,
        label: delta > 0 ? 'Pressure rising' : 'Pressure falling',
      });
    } else {
      bullets.push({ positive: false, label: 'Stable pressure' });
    }
  } else {
    const pressurePts = Number(factors.pressure);
    if (Number.isFinite(pressurePts)) {
      bullets.push({
        positive: pressurePts >= 50,
        label: pressurePts >= 50 ? 'Pressure shifting' : 'Stable pressure',
      });
    }
  }

  if (typeof w.wind_speed === 'number') {
    const speed = Math.round(w.wind_speed);
    const cardinal = w.wind_cardinal || getCardinalFromDegrees(w.wind_deg) || '';
    const windLabel = `Wind ${[cardinal, `${speed} mph`].filter(Boolean).join(' ')}`.trim();
    if (day.wind_optimal_match) {
      bullets.push({ positive: true, label: `${windLabel} (optimal)` });
    } else if (speed >= 14) {
      bullets.push({ positive: false, label: `${windLabel} (high)` });
    } else {
      const windPts = Number(factors.wind);
      bullets.push({
        positive: Number.isFinite(windPts) && windPts >= 50,
        label: windLabel,
      });
    }
  }

  if (sunrise > 0 && sunset > 0) {
    const majorDaylight = overlapSolunarDaylight(majors, sunrise, sunset);
    const minorDaylight = overlapSolunarDaylight(minors, sunrise, sunset);

    if (majorDaylight > 0) {
      bullets.push({ positive: true, label: 'Daylight major feeding window' });
    }
    if (majorDaylight > 0 && minorDaylight > 0) {
      bullets.push({ positive: true, label: 'Major + minor combo' });
    }

    const morningEnd = sunrise + 2 * 3600;
    const eveningStart = Math.max(sunrise, sunset - 2 * 3600);
    const majorCrep = Math.max(
      overlapSolunarWindow(majors, sunrise, morningEnd),
      overlapSolunarWindow(majors, eveningStart, sunset),
    );
    if (majorCrep >= 30) {
      bullets.push({ positive: true, label: 'Sunrise/sunset major bonus' });
    }

    const minorCrep = Math.max(
      overlapSolunarWindow(minors, sunrise, morningEnd),
      overlapSolunarWindow(minors, eveningStart, sunset),
    );
    if (minorCrep >= 30) {
      bullets.push({ positive: true, label: 'Sunrise/sunset minor bonus' });
    }
  } else if (majors.length > 0) {
    bullets.push({ positive: true, label: 'Major feeding period today' });
  }

  const wd = pythonWeekdayFromDate(day.date);
  if (wd != null) {
    if (wd >= 0 && wd <= 3) {
      bullets.push({ positive: true, label: 'Midweek bonus' });
    }
    if (wd === 5 || wd === 6) {
      bullets.push({ positive: false, label: 'Weekend penalty' });
    }
  }

  const effectiveTemp = w.temp_max ?? w.temp_day;
  if (effectiveTemp != null && effectiveTemp <= coolDayThresholdF) {
    bullets.push({ positive: true, label: `Cool day bonus (≤${Math.round(coolDayThresholdF)}°F)` });
  }

  if (isRainyDay(w)) {
    bullets.push({ positive: false, label: 'Rain penalty' });
  }

  if (day.wind_optimal_match && typeof w.wind_speed === 'number' && w.wind_speed < 14) {
    const alreadyNoted = bullets.some((b) => b.label.includes('optimal'));
    if (!alreadyNoted) {
      bullets.push({ positive: true, label: 'Optimal wind match bonus' });
    }
  }

  return bullets;
}
